import { Card, CardContent, Input, Switch, toast } from '@app/ui';
import { PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

const CONFIG_KEYS = [
  {
    key: 'maintenance_mode',
    label: 'Maintenance Mode',
    type: 'toggle',
    description: 'Enable to show maintenance page to all users',
  },
  {
    key: 'signup_enabled',
    label: 'Signup Enabled',
    type: 'toggle',
    description: 'Allow new user registrations',
  },
  {
    key: 'ai_enabled',
    label: 'AI Features Enabled',
    type: 'toggle',
    description: 'Enable AI deal extraction and valuation',
  },
  {
    key: 'max_free_deals',
    label: 'Max Free Deals',
    type: 'number',
    description: 'Maximum deals for free plan users',
  },
  {
    key: 'stripe_pro_price_id',
    label: 'Stripe Pro Price ID',
    type: 'text',
    description: 'Stripe price ID for Pro plan',
  },
] as const;

export const Route = createFileRoute('/admin/config')({
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(serverApi.admin.siteConfig.queryOptions());
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Config,
});

function Config() {
  const queryClient = useQueryClient();
  const { data: config } = useSuspenseQuery(api.admin.siteConfig.queryOptions());
  const [saving, setSaving] = useState<string | null>(null);

  const updateMutation = useMutation({
    ...api.admin.updateSiteConfig.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: api.admin.siteConfig.queryKey() });
      toast.success('Configuration saved');
    },
  });

  const handleUpdate = async (key: string, value: string) => {
    setSaving(key);
    try {
      await updateMutation.mutateAsync({ key, value });
    } catch (error) {
      console.error(error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(null);
    }
  };

  return (
    <PageTransition>
      <h1 className="text-2xl font-bold">Configuration</h1>
      <p className="mt-2 text-muted-foreground">Platform-wide settings and feature flags.</p>

      <StaggerContainer className="mt-6 space-y-4">
        {CONFIG_KEYS.map(({ key, label, type, description }) => (
          <StaggerItem key={key}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{label}</p>
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </div>
                  {type === 'toggle' ? (
                    <Switch
                      checked={config[key] === 'true'}
                      onCheckedChange={(checked) => handleUpdate(key, String(checked))}
                      disabled={saving === key}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        key={`${key}-${config[key] ?? ''}`}
                        type={type}
                        defaultValue={config[key] || ''}
                        className="w-48"
                        onBlur={(e) => {
                          if (e.target.value !== (config[key] || '')) {
                            handleUpdate(key, e.target.value);
                          }
                        }}
                      />
                      {saving === key && (
                        <span className="text-xs text-muted-foreground">Saving...</span>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </PageTransition>
  );
}
